import { Component, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { ToastService } from '../../../shared/services/toast.service';
import {
    UntypedFormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    Validators,
    UntypedFormArray,
} from '@angular/forms';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { forkJoin } from 'rxjs';
import {
    EmployeeYTD,
    EmployeeProjectionData,
    TotalMonthProjection,
    BranchProjectionOpenCloseDates,
    BranchProjectionData,
    BranchYTD,
    BranchProjectionServerData,
    ClientEmployeeMonthProjection,
    EmployeeTotals,
    Branch,
    Year,
} from './performance-projection.data';
import { ProjectionService } from '../../services/projection.service';
import { BranchService } from '../../../shared/services/branch.service';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';

@Component({
    selector: 'app-performance-projections-config',
    templateUrl: './performance-projections-config.component.html',
    styleUrls: ['./performance-projections-config.component.scss'],
})
export class PerformanceProjectionsConfigComponent implements OnInit {
    Arr = Array;
    public data = {
        loans: {
            current: null,
            projection: null,
        },
        volume: {
            current: null,
            projection: null,
        },
        purchase: {
            current: null,
            projection: null,
        },
        government: {
            current: null,
            projection: null,
        },
        tenured: {
            current: null,
        },
    };

    branchProjectionForm: UntypedFormGroup;
    branchIdentifier: string;
    branchesOfInterest: Branch[];
    selectedIdentifier: Branch[];
    yearsOfInterest: Year[];
    selectedYear: Year[];
    seasonalityCurveHardCodedDefault: number[] = [
        91, 93, 94, 95, 99, 108, 115, 118, 102, 97, 93, 95,
    ];
    today: string;
    showEmployeeProjections: boolean;
    employeeYTDStats: EmployeeYTD[];
    employeeMonthlyProjections: EmployeeProjectionData[];
    totalMonthlyProjections: TotalMonthProjection;
    adjustedMonthlyProjections: TotalMonthProjection;
    updateEmployeeProjectionsOnNextClicked: boolean;
    branchListSettings: any;
    yearListSettings: any;
    employeeProjectionData: EmployeeTotals[];
    projectionsAreEditable: boolean;
    projectionsAreBeingEdited: boolean;
    noProjections: boolean;
    projectionsAreOpen: boolean;
    addProjections: boolean;
    showSuccessfulSubmitMessage: boolean;
    viewingBranchProjections: boolean;
    branchProjectionSignificantDates: BranchProjectionOpenCloseDates[];
    openDate: Date;
    closingDate: Date;
    yearProjectionsAreOpen?: number;
    dates: BranchProjectionOpenCloseDates[];
    avgBranchLoanAmountForNewEmployee: number;
    previousBranchProjectionConfigData: BranchProjectionServerData;
    seasonalityCurve: number[];
    yearOption: string;
    loading: boolean;
    statDescription: string;
    submitLoading: boolean = false;
    monthlyShrinkStretchUnits: number[];
    monthlyShrinkStretchVol: number[];
    totalUnits: number;
    totalVol: number;
    constructor(
        private toast: ToastService,
        private projectionService: ProjectionService,
        private branchService: BranchService,
        private formBuilder: UntypedFormBuilder,
        private reusableFunctions: ReusableFunctions,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService
    ) {
        this.branchProjectionForm = this.createFormGroup();
        this.onBranchProjectionChanges();

        this.branchListSettings = {
            singleSelection: true,
            text: 'Select Branch',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'hierarchyName',
            primaryKey: 'hierarchyIdentifier',
            enableFilterSelectAll: false,
        };

        this.yearListSettings = {
            singleSelection: true,
            text: 'Select Year',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'year',
            primaryKey: 'year',
            enableFilterSelectAll: false,
        };
    }

    ngOnInit() {
        this.loading = true;
        this.yearsOfInterest = [];
        this.addYears();
        this.selectedYear = [this.yearsOfInterest[0]];
        this.yearOption = this.selectedYear[0].year;
        this.seasonalityCurve = this.seasonalityCurveHardCodedDefault;
        this.totalMonthlyProjections = { monthlyUnits: [], monthlyVolume: [] };
        this.adjustedMonthlyProjections = { monthlyUnits: [], monthlyVolume: [] };
        this.projectionsAreBeingEdited = false;
        this.noProjections = true;
        this.employeeMonthlyProjections = [];
        this.updateEmployeeProjectionsOnNextClicked = true;

        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        if (clientShortName === 'LBA' && !this.impersonationService.isImpersonatingClient()) {
            return;
        }

        //this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this.router.url);
        this.getBranches();
        this.showEmployeeProjections = false;
        this.projectionService.getBranchProjectionOpenCloseDates().subscribe((dates) => {
            this.significantDatesHandler(dates);
        });
    }

    getBranches() {
        this.branchService.getBranchesOfInterest().subscribe(
            (branches) => {
                if (branches && branches.length) {
                    this.branchesOfInterest = branches;
                    this.selectedIdentifier = [
                        this.branchesOfInterest.find((b) => b.hierarchyIdentifier),
                    ];
                    this.projectionService
                        .getYearsOfInterest(this.selectedIdentifier[0].hierarchyIdentifier)
                        .subscribe((years) => {
                            if (years && years.length) {
                                years.forEach((year) => {
                                    if (
                                        this.yearsOfInterest.findIndex(
                                            (y) => y.yearValue === year
                                        ) === -1
                                    ) {
                                        this.yearsOfInterest.push({
                                            year: year.toString(),
                                            yearValue: year,
                                        });
                                    }
                                });
                                this.addYears();
                                this.selectedYear = [this.yearsOfInterest[0]];
                            } else {
                                this.yearsOfInterest = [
                                    {
                                        year: new Date().getFullYear().toString(),
                                        yearValue: new Date().getFullYear(),
                                    },
                                    {
                                        year: (new Date().getFullYear() + 1).toString(),
                                        yearValue: new Date().getFullYear() + 1,
                                    },
                                ];
                                this.selectedYear = [this.yearsOfInterest[0]];
                            }
                            this.setBranch();
                        });
                } else {
                    this.toast.warning('No branches of interest found for current user.');
                    this.loading = false;
                }
            },
            (error) => {
                console.log(error);
                this.loading = false;
                throw new Error('Problem retrieving the branches.');
            }
        );
    }

    addYears() {
        const thisYear = new Date().getFullYear();
        if (this.yearsOfInterest.findIndex((y) => y.yearValue === thisYear) === -1) {
            this.yearsOfInterest.push(
                {
                    year: new Date().getFullYear().toString(),
                    yearValue: new Date().getFullYear(),
                },
                {
                    year: (new Date().getFullYear() + 1).toString(),
                    yearValue: new Date().getFullYear() + 1,
                }
            );
        }

        this.sortYearsData();
    }

    sortYearsData() {
        this.yearsOfInterest?.sort((a, b) => a.yearValue - b.yearValue);
    }

    onBranchProjectionChanges() {
        this.totalLoans.valueChanges.subscribe((val) => {
            this.branchProjectionForm.markAsDirty();
            this.unitsPerMonth.setValue(Math.round(val / 12));
            if (this.tenuredProducers) {
                this.unitsPerProducer.setValue(
                    Math.round(this.unitsPerMonth.value / this.tenuredProducers.value)
                );
            }
            if (this.totalVolume) {
                this.avgLoanAmount.setValue(Math.round(this.totalVolume.value / val));
            }
            this.adjustedMonthlyProjections.monthlyUnits =
                this.applyUnitSeasonailityCurveAlgorithm(val);
        });
        this.totalVolume.valueChanges.subscribe((val) => {
            this.branchProjectionForm.markAsDirty();
            this.totalMonthlyVolume.setValue(Math.round(val / 12));
            if (this.purchaseMix) {
                this.purchaseVolume.setValue(Math.round((val * this.purchaseMix.value) / 100));
            }
            if (this.governmentMix) {
                this.governmentVolume.setValue(Math.round((val * this.governmentMix.value) / 100));
            }
            if (this.totalLoans) {
                this.avgLoanAmount.setValue(Math.round(val / this.totalLoans.value));
            }
            this.adjustedMonthlyProjections.monthlyVolume =
                this.applyVolumeSeasonalityCurveAlgrithm(val);
        });
        this.purchaseMix.valueChanges.subscribe((val) => {
            this.branchProjectionForm.markAsDirty();
            if (!this.totalVolume.value) {
                return;
            }
            this.purchaseVolume.setValue(Math.round((this.totalVolume.value * val) / 100));
        });
        this.governmentMix.valueChanges.subscribe((val) => {
            this.branchProjectionForm.markAsDirty();
            if (!this.totalVolume.value) {
                return;
            }
            this.governmentVolume.setValue(Math.round((this.totalVolume.value * val) / 100));
        });
        this.tenuredProducers.valueChanges.subscribe((val) => {
            this.branchProjectionForm.markAsDirty();
            this.unitsPerProducer.setValue(Math.round(this.unitsPerMonth.value / val));
        });
    }

    onEmployeeProjectionChanges() {
        this.employeeProjections.forEach((employeeProjection, index) => {
            employeeProjection.get('totalEmployeeUnits').valueChanges.subscribe((employeeUnits) => {
                this.employeeUnitsChangedHandler(employeeUnits, index);
                this.branchProjectionForm.markAsDirty();
            });
            employeeProjection
                .get('totalEmployeeVolume')
                .valueChanges.subscribe((employeeVolume) => {
                    this.employeeVolumeChangedHandler(employeeVolume, index);
                    this.branchProjectionForm.markAsDirty();
                });
        });
    }

    employeeVolumeChangedHandler(employeeVolume: number, index: number) {
        const monthlyVolume: number[] = this.applyVolumeSeasonalityCurveAlgrithm(
            employeeVolume,
            this.employeeYTDStats[index]?.termMo
        );
        this.updateEmployeeMonthlyProjections(
            'volume',
            this.employeeMonthlyProjections[index],
            monthlyVolume
        );
        let totalVolumeOfFTProducers: number = 0;
        this.employeeProjections.forEach((employeeProjection) => {
            totalVolumeOfFTProducers += +employeeProjection.get('totalEmployeeVolume').value;
        });
        this.shrinkStretchVolume.setValue(this.calculateShrinkStretch());
        this.applyMonthlyShrinkStretch();
    }

    calculateShrinkStretch(): any {
        if (this.employeeProjections && this.employeeProjections.length > 0) {
            let totalEmployeeProjectedVolume = 0;
            this.employeeProjections.forEach((employee) => {
                totalEmployeeProjectedVolume += employee.get('totalEmployeeVolume').value;
            });
            return this.totalVolume.value - totalEmployeeProjectedVolume;
        } else {
            return this.totalVolume.value;
        }
    }

    updateEmployeeMonthlyProjections(
        typeToUpdate: string,
        employeeProjection: EmployeeProjectionData,
        monthlyValues: number[]
    ) {
        if (employeeProjection.monthlyProjections.length === 0) {
            //initialize
            employeeProjection;
            for (let i = 0; i < 12; i++) {
                employeeProjection.monthlyProjections.push({
                    primaryIdentifier: employeeProjection.primaryIdentifier,
                    units: 0,
                    volume: 0,
                    YearMonth:
                        this.selectedYear[0].year +
                        '-' +
                        (i < 9 ? '0' + (i + 1).toString() : (i + 1).toString()),
                });
            }
        }
        if (typeToUpdate === 'volume') {
            employeeProjection.monthlyProjections.forEach((month, i) => {
                month.volume = monthlyValues[i];
            });
        } else if (typeToUpdate === 'units') {
            employeeProjection.monthlyProjections.forEach((month, i) => {
                month.units = monthlyValues[i];
            });
        }
    }

    employeeUnitsChangedHandler(employeeUnits: number, index: number) {
        const monthlyUnits: number[] = this.applyUnitSeasonailityCurveAlgorithm(
            employeeUnits,
            this.employeeYTDStats[index]?.termMo
        );
        this.updateEmployeeMonthlyProjections(
            'units',
            this.employeeMonthlyProjections[index],
            monthlyUnits
        );
        this.employeeProjections[index]
            .get('totalEmployeeVolume')
            .setValue(
                Math.round(
                    Math.round(this.employeeYTDStats[index]?.avgLoanAmount) === 0
                        ? this.avgBranchLoanAmountForNewEmployee * employeeUnits
                        : Math.round(this.employeeYTDStats[index]?.avgLoanAmount) * employeeUnits
                )
            );
        let totalUnitsOfFTProducers: number = 0;
        this.employeeProjections.forEach((employeeProjection) => {
            totalUnitsOfFTProducers += +employeeProjection.get('totalEmployeeUnits').value;
        });
        this.shrinkStretchUnits.setValue(this.totalLoans.value - totalUnitsOfFTProducers);
        this.applyMonthlyShrinkStretch();
    }

    applyVolumeSeasonalityCurveAlgrithm(
        employeeVolume: number,
        numMonthsProjectedToWork?: number
    ): number[] {
        const monthlyVolume: number[] = [];
        numMonthsProjectedToWork = numMonthsProjectedToWork > 0 ? numMonthsProjectedToWork : 12;
        for (let i = 0; i < 12; i++) {
            const percentage = this.seasonalityCurve[i] / 100;
            const adjustedVolum =
                i + 1 > numMonthsProjectedToWork
                    ? 0
                    : (employeeVolume / numMonthsProjectedToWork) * percentage;
            const roundedVolume = Math.round(adjustedVolum);
            monthlyVolume.push(roundedVolume);
        }
        const sumOfParts = monthlyVolume.reduce((a, b) => a + b, 0);
        const diff = employeeVolume - sumOfParts;
        monthlyVolume[0] = monthlyVolume[0] + diff;
        return monthlyVolume;
    }

    adjustVolumesSoTheySumToEmployeeVolume(
        monthlyVolume: number[],
        diff: number,
        employeeVolume: number
    ) {
        if (diff === 0) {
            return;
        }
        const addition: boolean = diff > 0;
        const adjustment = diff / 12;
        for (let i = 0; i < 12; i++) {
            if (addition) {
                monthlyVolume[i] = monthlyVolume[i] + adjustment;
            } else {
                monthlyVolume[i] = monthlyVolume[i] - adjustment;
            }
        }
        const newTotal = monthlyVolume.reduce((a, b) => a + b, 0);
        const newDiff = employeeVolume - newTotal;
        this.makeFinalAdjustment(monthlyVolume, newDiff);
    }

    makeFinalAdjustment(monthlyVolume: number[], newDiff: number) {
        if (newDiff === 0) {
            return;
        }
        const addition: boolean = newDiff > 0;
        if (addition) {
            const indexOfHighest = this.reusableFunctions.indexOfMax(this.seasonalityCurve);
            monthlyVolume[indexOfHighest] = monthlyVolume[indexOfHighest] + newDiff;
        } else {
            const indexOfLowest = this.reusableFunctions.indexOfMin(this.seasonalityCurve);
            monthlyVolume[indexOfLowest] = monthlyVolume[indexOfLowest] - newDiff;
        }
    }

    applyMonthlyShrinkStretch() {
        this.monthlyShrinkStretchUnits = [];
        this.monthlyShrinkStretchVol = [];
        this.totalMonthlyProjections.monthlyUnits = [];
        this.totalMonthlyProjections.monthlyVolume = [];
        for (let i = 0; i < 12; i++) {
            let totalMonthlyProjectedUnits = 0;
            let totalMonthlyProjectedVol = 0;
            this.employeeMonthlyProjections.forEach((emp, j) => {
                totalMonthlyProjectedUnits +=
                    emp.monthlyProjections.length > i ? emp.monthlyProjections[i]?.units ?? 0 : 0;
                totalMonthlyProjectedVol +=
                    emp.monthlyProjections.length > i ? emp.monthlyProjections[i]?.volume ?? 0 : 0;
            });
            this.monthlyShrinkStretchUnits.push(
                this.adjustedMonthlyProjections.monthlyUnits[i] - totalMonthlyProjectedUnits
            );
            this.monthlyShrinkStretchVol.push(
                this.adjustedMonthlyProjections.monthlyVolume[i] - totalMonthlyProjectedVol
            );
            this.totalMonthlyProjections.monthlyUnits.push(totalMonthlyProjectedUnits);
            this.totalMonthlyProjections.monthlyVolume.push(totalMonthlyProjectedVol);
        }
        this.totalUnits = this.totalMonthlyProjections.monthlyUnits.reduce((a, b) => a + b, 0);
        this.totalVol = this.totalMonthlyProjections.monthlyVolume.reduce((a, b) => a + b, 0);
    }

    applyUnitSeasonailityCurveAlgorithm(
        employeeUnits: number,
        numMonthsProjectedToWork?: number
    ): number[] {
        const monthlyUnitsRounded: number[] = [];
        const roundedValues: number[] = [];
        numMonthsProjectedToWork = numMonthsProjectedToWork > 0 ? numMonthsProjectedToWork : 12;
        for (let i = 0; i < 12; i++) {
            const unitsRaw =
                i + 1 > numMonthsProjectedToWork
                    ? 0
                    : ((employeeUnits / numMonthsProjectedToWork) *
                          this.seasonalityCurveHardCodedDefault[i]) /
                      100;
            const unitsRounded = this.reusableFunctions.round(unitsRaw, 0);
            monthlyUnitsRounded.push(unitsRounded);
            roundedValues.push(unitsRounded - unitsRaw);
        }
        const diff = monthlyUnitsRounded.reduce((a, b) => a + b, 0) - employeeUnits;
        if (diff) {
            return this.normalizeMonthlyUnits(
                monthlyUnitsRounded,
                diff,
                roundedValues,
                numMonthsProjectedToWork
            );
        } else {
            return monthlyUnitsRounded;
        }
    }

    normalizeMonthlyUnits(
        monthlyUnits: number[],
        diff: number,
        roundedValues: number[],
        monthsToConsider: number
    ): number[] {
        const isDiffNegative = diff < 0;
        for (let i = 0; i < Math.abs(diff); i++) {
            if (isDiffNegative) {
                const indexOfMin = this.reusableFunctions.indexOfMin(
                    roundedValues.slice(0, monthsToConsider)
                );
                monthlyUnits[indexOfMin] = monthlyUnits[indexOfMin] + 1;
                roundedValues[indexOfMin] = Infinity; //so we can find the next lowest value if needed
            } else {
                const indexOfMax = this.reusableFunctions.indexOfMax(
                    roundedValues.slice(0, monthsToConsider)
                );
                monthlyUnits[indexOfMax] = monthlyUnits[indexOfMax] - 1;
                roundedValues[indexOfMax] = -Infinity; //so we can find the next highest value if needed
            }
        }
        return monthlyUnits;
    }

    updateEmployeeVolumeProjections() {
        const totalEmployeeProjectedVolume = this.calculateTotalEmployeeProjectedVolume();
        this.shrinkStretchVolume.setValue(totalEmployeeProjectedVolume - this.totalVolume.value);
        this.employeeProjections.forEach((employeeProjection) => {
            employeeProjection
                .get('totalEmployeeVolume')
                .setValue(Math.round(totalEmployeeProjectedVolume));
        });
    }

    calculateTotalEmployeeProjectedVolume() {
        let total = 0;
        this.employeeProjections.forEach((employeeProjection) => {
            total += employeeProjection.get('totalEmployeeVolume').value;
        });
        return total;
    }

    updateEmployeeProjections() {
        if (!this.totalLoans.dirty && !this.totalVolume.dirty) {
            this.employeeProjections.forEach((employeeProjection, i) => {
                this.updateEmployeeMonthlyProjections(
                    'units',
                    this.employeeMonthlyProjections[i],
                    this.applyUnitSeasonailityCurveAlgorithm(
                        employeeProjection.get('totalEmployeeUnits').value,
                        this.employeeYTDStats[i]?.termMo
                    )
                );
                this.updateEmployeeMonthlyProjections(
                    'volume',
                    this.employeeMonthlyProjections[i],
                    this.applyUnitSeasonailityCurveAlgorithm(
                        employeeProjection.get('totalEmployeeVolume').value,
                        this.employeeYTDStats[i]?.termMo
                    )
                );
            });
        } else {
            let tenuredEmployeeProjectedUnits = 0;
            let tenuredEmplyoeeProjectedVolume = 0;
            this.employeeProjections.forEach((employeeProjection, i) => {
                let employeeProjectedUnits =
                    this.employeeYTDStats[i].avgFundedUnits *
                    (this.employeeYTDStats[i].termMo > 0 ? this.employeeYTDStats[i].termMo : 12);
                let employeeProjectedVolume =
                    this.employeeYTDStats[i].avgFundedVol *
                    (this.employeeYTDStats[i].termMo > 0 ? this.employeeYTDStats[i].termMo : 12);
                if (employeeProjectedUnits === 0) {
                    employeeProjectedUnits = 12;
                }
                if (employeeProjectedVolume === 0) {
                    this.avgBranchLoanAmountForNewEmployee = Math.round(
                        this.calculateAvgLoanAmountForNewEmployee()
                    );
                    employeeProjectedVolume = this.avgBranchLoanAmountForNewEmployee * 12;
                }
                employeeProjection.get('totalEmployeeUnits').setValue(employeeProjectedUnits);
                this.updateEmployeeMonthlyProjections(
                    'units',
                    this.employeeMonthlyProjections[i],
                    this.applyUnitSeasonailityCurveAlgorithm(
                        employeeProjectedUnits,
                        this.employeeYTDStats[i]?.termMo
                    )
                );
                employeeProjection
                    .get('totalEmployeeVolume')
                    .setValue(Math.round(employeeProjectedVolume));
                this.updateEmployeeMonthlyProjections(
                    'units',
                    this.employeeMonthlyProjections[i],
                    this.applyUnitSeasonailityCurveAlgorithm(
                        employeeProjectedUnits,
                        this.employeeYTDStats[i]?.termMo
                    )
                );
                tenuredEmployeeProjectedUnits += employeeProjectedUnits;
                tenuredEmplyoeeProjectedVolume += employeeProjectedVolume;
            });
            this.shrinkStretchUnits.setValue(this.totalLoans.value - tenuredEmployeeProjectedUnits);
            this.shrinkStretchVolume.setValue(
                this.totalVolume.value - tenuredEmplyoeeProjectedVolume
            );
        }
        this.applyMonthlyShrinkStretch();
    }

    calculateAvgLoanAmountForNewEmployee() {
        //find min average greater than 0 and use that for new employee loan amount average.
        let minLoanAmountAvg = Infinity;
        this.employeeYTDStats.forEach((employeeStats) => {
            if (employeeStats.avgFundedVol > 0 && employeeStats.avgFundedVol < minLoanAmountAvg) {
                minLoanAmountAvg = employeeStats.avgFundedVol;
            }
        });
        return minLoanAmountAvg;
    }

    getThisEmployeeProjectionControlGroup(index: number) {
        return this.employeeProjections[index];
    }

    getEmployeeUnitsControl(index: number) {
        return this.employeeProjections[index].get('totalEmployeeUnits');
    }

    getEmployeeVolumeControl(index: number) {
        return this.employeeProjections[index].get('totalEmployeeVolume');
    }

    get shrinkStretchUnits() {
        return this.branchProjectionForm.get('branchProjections').get('shrinkStretchUnits');
    }

    get shrinkStretchVolume() {
        return this.branchProjectionForm.get('branchProjections').get('shrinkStretchVolume');
    }

    get branchProjections() {
        return this.branchProjectionForm.get('branchProjections');
    }

    get employeeProjections() {
        return (<UntypedFormArray>this.branchProjectionForm.get('employeeProjections')).controls;
    }

    get totalLoans() {
        return this.branchProjectionForm.get('branchProjections').get('totalLoans');
    }

    get totalVolume() {
        return this.branchProjectionForm.get('branchProjections').get('totalVolume');
    }

    get purchaseMix() {
        return this.branchProjectionForm.get('branchProjections').get('purchaseMix');
    }

    get governmentMix() {
        return this.branchProjectionForm.get('branchProjections').get('governmentMix');
    }

    get tenuredProducers() {
        return this.branchProjectionForm.get('branchProjections').get('tenuredProducers');
    }

    get unitsPerMonth() {
        return this.branchProjectionForm.get('branchProjections').get('unitsPerMonth');
    }

    get avgLoanAmount() {
        return this.branchProjectionForm.get('branchProjections').get('avgLoanAmount');
    }

    get totalMonthlyVolume() {
        return this.branchProjectionForm.get('branchProjections').get('totalMonthlyVolume');
    }

    get purchaseVolume() {
        return this.branchProjectionForm.get('branchProjections').get('purchaseVolume');
    }

    get governmentVolume() {
        return this.branchProjectionForm.get('branchProjections').get('governmentVolume');
    }

    get unitsPerProducer() {
        return this.branchProjectionForm.get('branchProjections').get('unitsPerProducer');
    }

    get hierarchyIdentifier() {
        return this.branchProjectionForm.get('branchProjections').get('hierarchyIdentifier');
    }

    get year() {
        return this.branchProjectionForm.get('branchProjections').get('year');
    }

    get isDraft() {
        return this.branchProjectionForm.get('branchProjections').get('isDraft');
    }

    createFormGroup(): any {
        return this.formBuilder.group({
            branchProjections: this.formBuilder.group({
                totalLoans: new UntypedFormControl(0, [
                    Validators.required,
                    Validators.max(999999),
                ]),
                totalVolume: new UntypedFormControl(0, [
                    Validators.required,
                    Validators.max(9999999999999),
                ]),
                purchaseMix: new UntypedFormControl(0, [
                    Validators.required,
                    Validators.min(0),
                    Validators.max(100),
                ]),
                governmentMix: new UntypedFormControl(0, [
                    Validators.required,
                    Validators.min(0),
                    Validators.max(100),
                ]),
                tenuredProducers: new UntypedFormControl(0, [
                    Validators.required,
                    Validators.max(999999),
                ]),
                unitsPerMonth: new UntypedFormControl(0, Validators.required),
                avgLoanAmount: new UntypedFormControl(0, Validators.required),
                totalMonthlyVolume: new UntypedFormControl(0, Validators.required),
                purchaseVolume: new UntypedFormControl(0, Validators.required),
                governmentVolume: new UntypedFormControl(0, Validators.required),
                isDraft: new UntypedFormControl(),
                shrinkStretchUnits: new UntypedFormControl(),
                shrinkStretchVolume: new UntypedFormControl(),
                hierarchyIdentifier: new UntypedFormControl('', Validators.required),
                year: new UntypedFormControl(),
                unitsPerProducer: new UntypedFormControl(0, Validators.required),
            }),
            employeeProjections: this.formBuilder.array([], Validators.required),
        });
    }

    nextClicked() {
        if (this.updateEmployeeProjectionsOnNextClicked) {
            this.updateEmployeeProjections();
        }
        this.showEmployeeProjections = true;
        this.projectionsAreBeingEdited = true;
    }

    onSubmit() {
        this.submitLoading = true;
        this.branchProjectionForm.markAsPristine();
        if (!this.isSeasonalityCurveAve100()) {
            this.toast.warning('Seanonality curve must average to exactly 100.');
            this.submitLoading = false;
            return;
            //don't allow submission
        }
        this.isDraft.setValue(false);
        this.saveProjections();
    }

    isSeasonalityCurveAve100(): boolean {
        let ave = this.seasonalityCurve.reduce((a, b) => a + b, 0) / 12;
        return ave === 100;
    }

    private saveProjections() {
        if (this.branchIdentifier) {
            this.year.setValue(this.selectedYear[0].year);
            if (!this.showEmployeeProjections) {
                this.updateEmployeeProjections();
                this.updateEmployeeVolumeProjections();
            }
            this.projectionService
                .saveBranchProjection({
                    branchProjectionData: this.convertBranchProjectionTypes(),
                    seasonalityCurve: this.seasonalityCurve,
                    employeeProjectionData: this.employeeMonthlyProjections,
                    projectionEmployeeYTDData: this.employeeYTDStats ?? [],
                })
                .subscribe(
                    () => {
                        this.showSuccessfulSubmitMessage = true;
                        this.submitLoading = false;
                        this.branchProjectionForm.markAsPristine();
                    },
                    () => {
                        this.toast.warning(
                            "Something went wrong saving your branch's projections."
                        );
                        this.submitLoading = false;
                    }
                );
        }
    }

    convertBranchProjectionTypes(): BranchProjectionData {
        return {
            hierarchyIdentifier: this.hierarchyIdentifier.value,
            year: +this.year.value,
            totalLoans: +this.totalLoans.value,
            totalVolume: Math.round(+this.totalVolume.value),
            purchaseMix: +this.purchaseMix.value,
            governmentMix: +this.governmentMix.value,
            tenuredProducers: +this.tenuredProducers.value,
            unitsPerMonth: +this.unitsPerMonth.value,
            avgLoanAmount: Math.round(+this.avgLoanAmount.value),
            totalMonthlyVolume: Math.round(+this.totalMonthlyVolume.value),
            purchaseVolume: Math.round(+this.purchaseVolume.value),
            governmentVolume: +this.governmentVolume.value,
            unitsPerProducer: +this.unitsPerProducer.value,
            isDraft: this.isDraft.value,
            shrinkStretchUnits: +this.shrinkStretchUnits.value,
            shrinkStretchVolume: Math.round(+this.shrinkStretchVolume.value),
        };
    }

    public getBranchProjectionHandler(branchProjectionData: BranchProjectionServerData) {
        if (branchProjectionData && branchProjectionData.branchProjectionData) {
            if (
                branchProjectionData.seasonalityCurve &&
                branchProjectionData.seasonalityCurve.length === 12
            ) {
                this.seasonalityCurve = branchProjectionData.seasonalityCurve;
            } else {
                this.seasonalityCurve = this.seasonalityCurveHardCodedDefault;
            }

            this.employeeProjectionData = this.formatServerData(
                branchProjectionData.employeeProjectionData
            );
            // Input data
            if (branchProjectionData.branchProjectionData.totalLoans) {
                this.totalLoans.setValue(branchProjectionData.branchProjectionData.totalLoans);
            }
            if (branchProjectionData.branchProjectionData.totalVolume) {
                this.totalVolume.setValue(branchProjectionData.branchProjectionData.totalVolume);
            }
            if (branchProjectionData.branchProjectionData.governmentMix) {
                this.governmentMix.setValue(
                    branchProjectionData.branchProjectionData.governmentMix
                );
            }
            if (branchProjectionData.branchProjectionData.purchaseMix) {
                this.purchaseMix.setValue(branchProjectionData.branchProjectionData.purchaseMix);
            }
            if (branchProjectionData.branchProjectionData.tenuredProducers) {
                this.tenuredProducers.setValue(
                    branchProjectionData.branchProjectionData.tenuredProducers
                );
            }

            this.shrinkStretchUnits.setValue(
                branchProjectionData.branchProjectionData.shrinkStretchUnits
            );
            this.shrinkStretchVolume.setValue(
                branchProjectionData.branchProjectionData.shrinkStretchVolume
            );

            // Result data
            if (branchProjectionData.branchProjectionData.unitsPerMonth) {
                this.unitsPerMonth.setValue(
                    branchProjectionData.branchProjectionData.unitsPerMonth
                );
            }
            if (branchProjectionData.branchProjectionData.avgLoanAmount) {
                this.avgLoanAmount.setValue(
                    branchProjectionData.branchProjectionData.avgLoanAmount
                );
            }
            if (branchProjectionData.branchProjectionData.totalMonthlyVolume) {
                this.totalMonthlyVolume.setValue(
                    branchProjectionData.branchProjectionData.totalMonthlyVolume
                );
            }
            if (branchProjectionData.branchProjectionData.purchaseVolume) {
                this.purchaseVolume.setValue(
                    branchProjectionData.branchProjectionData.purchaseVolume
                );
            }
            if (branchProjectionData.branchProjectionData.governmentVolume) {
                this.governmentVolume.setValue(
                    branchProjectionData.branchProjectionData.governmentVolume
                );
            }
            if (branchProjectionData.branchProjectionData.unitsPerProducer) {
                this.unitsPerProducer.setValue(
                    branchProjectionData.branchProjectionData.unitsPerProducer
                );
            }
        } else {
            this.branchProjectionForm.reset();
            this.hierarchyIdentifier.setValue(this.branchIdentifier);
            if (!this.seasonalityCurve) {
                this.seasonalityCurve = this.seasonalityCurveHardCodedDefault;
            }
        }
    }
    formatServerData(clientEmployeeProjections: ClientEmployeeMonthProjection[]): EmployeeTotals[] {
        const employeeProjectionData: EmployeeTotals[] = [];
        const primaryIdentifierSet = new Set();
        clientEmployeeProjections.forEach((empProj) => {
            if (primaryIdentifierSet.has(empProj.primaryIdentifier)) {
                const employeeData = employeeProjectionData.find(
                    (empProjMonth) => empProjMonth.primaryIdentifier === empProj.primaryIdentifier
                );
                employeeData.totalUnits = employeeData.totalUnits + empProj.units;
                employeeData.totalVolume = employeeData.totalVolume + empProj.volume;
            } else {
                //first time seeing this primary identifier in the list
                employeeProjectionData.push({
                    primaryIdentifier: empProj.primaryIdentifier,
                    totalUnits: empProj.units,
                    totalVolume: empProj.volume,
                });
                primaryIdentifierSet.add(empProj.primaryIdentifier);
            }
        });
        return employeeProjectionData;
    }

    public setBranch() {
        this.branchIdentifier = this.selectedIdentifier[0].hierarchyIdentifier;
        this.yearOption = this.selectedYear[0].year;
        if (this.dates) {
            this.significantDatesHandler(this.dates);
        }
        this.projectionsAreEditable = false;
        this.projectionsAreBeingEdited = false;
        this.addProjections = false;
        this.branchProjectionForm.reset();
        this.employeeMonthlyProjections = [];
        (<UntypedFormArray>this.branchProjectionForm.get('employeeProjections')).clear();
        this.showEmployeeProjections = false;
        try {
            if (this.branchIdentifier) {
                this.retrieveBranchData();
            }
        } catch (e) {}
    }

    showNoProjectionsMessage() {
        return (
            this.noProjections &&
            +this.selectedYear[0].year === new Date().getUTCFullYear() &&
            !this.projectionsAreOpen
        );
    }

    retrieveBranchData() {
        this.today = new Date().toLocaleDateString();
        this.branchProjectionForm.reset();
        this.hierarchyIdentifier.setValue(this.branchIdentifier);
        this.employeeProjectionData = undefined;
        this.projectionService
            .getBranchProjection(this.branchIdentifier, +this.selectedYear[0].year)
            .subscribe(
                (branchProjectionData) => {
                    if (branchProjectionData) {
                        this.previousBranchProjectionConfigData = branchProjectionData;
                        this.noProjections = false;
                        this.getBranchProjectionHandler(branchProjectionData);
                    } else {
                        this.noProjections = true;
                        this.projectionsAreEditable = true;
                        this.seasonalityCurve = this.seasonalityCurveHardCodedDefault;
                    }
                    forkJoin([
                        this.projectionService.getBranchYTD(
                            this.branchIdentifier,
                            this.decideWhichYearToRetrieve()
                        ),
                        this.projectionService.getEmployeeYTDProjHelper(
                            this.branchIdentifier,
                            this.decideWhichYearToRetrieve(),
                            branchProjectionData?.branchProjectionData?.clientProjectionBranchKey
                        ),
                    ]).subscribe(
                        ([branchYTDData, employeeYTDStats]) => {
                            this.getEmployeeYTDProjHelperHandler(employeeYTDStats);
                            this.getBranchYTDHandler(branchYTDData);
                            this.onEmployeeProjectionChanges();
                        },
                        (err) => {
                            throw new Error(err);
                        }
                    );
                },
                (err) => {
                    throw new Error(err);
                }
            );
    }

    decideWhichYearToRetrieve(): number {
        const currentYear = new Date().getFullYear();
        if (this.selectedYear[0].yearValue === currentYear) {
            this.statDescription = (currentYear - 1).toString() + ' actual was ';
            return currentYear - 1;
        } else {
            this.statDescription = 'YTD actual is ';
            return currentYear;
        }
    }

    significantDatesHandler(dates: BranchProjectionOpenCloseDates[]) {
        this.dates = dates;
        if (+this.selectedYear[0].year === new Date().getUTCFullYear()) {
            this.openDate = dates[0].openDate;
            this.closingDate = dates[0].closingDate;
        } else if (+this.selectedYear[0].year === new Date().getUTCFullYear() + 1) {
            this.openDate = dates[1].openDate;
            this.closingDate = dates[1].closingDate;
        }
        this.closingDate = new Date(
            this.reusableFunctions
                .getDateFromDateTimeOffset(this.closingDate)
                .setUTCHours(23, 59, 59)
        );
        this.projectionsAreOpen = this.areProjectionsOpenAtThisTime();
        if (this.projectionsAreOpen) {
            this.yearProjectionsAreOpen =
                this.openDate === dates[0].openDate
                    ? dates[0].projectionYear
                    : dates[1].projectionYear;
        }
    }

    areProjectionsOpenAtThisTime(): boolean {
        const currentDate = new Date();
        return currentDate <= new Date(this.closingDate) && currentDate >= new Date(this.openDate);
    }

    getEmployeeYTDProjHelperHandler(employeeYTDStats: EmployeeYTD[]) {
        this.employeeYTDStats = employeeYTDStats;
        if (this.employeeProjectionData) {
            this.addControlsForEmployees(this.employeeProjectionData);
            this.updateEmployeeProjectionsOnNextClicked = false;
            this.employeeProjectionData.forEach((employeeProjection, i) => {
                if (this.employeeProjections[i]) {
                    this.employeeProjections[i]
                        .get('employeeIdentifier')
                        .setValue(employeeProjection.primaryIdentifier);
                    this.employeeProjections[i]
                        .get('totalEmployeeUnits')
                        .setValue(employeeProjection.totalUnits);
                    this.employeeProjections[i]
                        .get('totalEmployeeVolume')
                        .setValue(employeeProjection.totalVolume);
                }
            });
            if (!this.projectionsAreEditable) {
                this.updateEmployeeProjections();
            }
            this.updateEmployeeProjectionsOnNextClicked = true;
            this.branchProjectionForm.markAsPristine();
        } else if (!!employeeYTDStats) {
            this.addControlsForEmployees(employeeYTDStats);
        }
        this.loading = false;
    }
    calculateTotal(monthlyValues: number[]): any {
        return monthlyValues.reduce((a, b) => a + b);
    }

    addControlsForEmployees(employeeYTDStats: EmployeeTotals[] | EmployeeYTD[]) {
        employeeYTDStats.forEach((employeeYTD) => {
            let identifier = employeeYTD.primaryIdentifier ?? employeeYTD.employeeIdentifier;
            const employeeProjection = this.formBuilder.group({
                employeeIdentifier: new UntypedFormControl(identifier, Validators.required),
                totalEmployeeUnits: new UntypedFormControl('', [
                    Validators.required,
                    Validators.max(9999),
                ]),
                totalEmployeeVolume: new UntypedFormControl('', [
                    Validators.required,
                    Validators.max(6000000000),
                ]),
            });
            this.employeeProjections.push(employeeProjection);
            this.employeeMonthlyProjections.push({
                primaryIdentifier: identifier,
                monthlyProjections: [],
            });
        });
    }

    private getBranchYTDHandler(branchYTDData: BranchYTD) {
        if (branchYTDData) {
            try {
                this.data.loans.current = branchYTDData.ytdUnits;
                this.data.volume.current = branchYTDData.ytdVol;
                this.data.purchase.current = branchYTDData.purchase;
                this.data.government.current = branchYTDData.govt;
                this.data.tenured.current = branchYTDData.tenured;
            } catch (e) {}
        } else {
            this.data = {
                loans: {
                    current: null,
                    projection: null,
                },
                volume: {
                    current: null,
                    projection: null,
                },
                purchase: {
                    current: null,
                    projection: null,
                },
                government: {
                    current: null,
                    projection: null,
                },
                tenured: {
                    current: null,
                },
            };
        }
    }

    //applyDisplayOptions(event: DisplayOptions[]) {
    //    this.displayOptions = event;
    //    this.projectionService.getOpenClosedDates()
    //        .subscribe(dates => { this.significantDatesHandler(dates) });
    //    this.setBranch();
    //}

    onBranchChange(branch: Branch) {
        if (branch) {
            this.selectedIdentifier = [branch];
            this.setBranch();
        }
    }

    onYearChange(year: Year) {
        if (year) {
            this.selectedYear = [year];
            if (this.selectedIdentifier) {
                this.setBranch();
            }
        }
    }

    onItemDeSelect(event) {}
    onSelectAll(event) {}
    onDeSelectAll(event) {}

    editEmployeeProjectionsClicked() {
        this.projectionsAreEditable = true;
        this.showEmployeeProjections = true;
        this.projectionsAreBeingEdited = true;
    }

    editBranchProjectionsClicked() {
        this.projectionsAreBeingEdited = true;
        this.projectionsAreEditable = true;
        this.showEmployeeProjections = false;
    }

    addProjectionsClicked() {
        this.addProjections = true;
    }

    viewClickedAfterSuccess() {
        this.showSuccessfulSubmitMessage = false;
        this.projectionsAreBeingEdited = false;
        this.projectionsAreEditable = false;
        scroll(0, 0);
    }

    editClickedAfterSuccess() {
        this.showSuccessfulSubmitMessage = false;
        this.projectionsAreBeingEdited = true;
        this.projectionsAreEditable = true;
        scroll(0, 0);
    }

    topRightXClickedAfterSuccess() {
        this.viewClickedAfterSuccess();
        scroll(0, 0);
    }

    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 27) {
            //keyCode 27 is the escape key.
            this.showSuccessfulSubmitMessage = false;
            this.viewClickedAfterSuccess();
        }
    }

    discardClicked() {
        this.projectionsAreBeingEdited = false;
        this.branchProjectionForm.reset();
        this.setBranch();
    }

    anyEmployeeProjectionIsInvalid() {
        return this.employeeProjections.some((employeeProjection) => {
            return (
                !employeeProjection.get('totalEmployeeUnits').valid ||
                !employeeProjection.get('totalEmployeeVolume').valid
            );
        });
    }

    showProjectionsAreNotOpenYetMessage() {
        if (this.openDate) {
            return (
                +this.selectedYear[0].year >= new Date().getUTCFullYear() &&
                new Date() < new Date(this.openDate)
            );
        }
        return false;
    }

    showProjectionsAreAlreadyClosedMessage() {
        return (
            !this.projectionsAreOpen && +this.selectedYear[0].year === new Date().getUTCFullYear()
        );
    }
}
